import { Injectable } from "@angular/core";
import { HttpHelper } from "./http.service";
import { BehaviorSubject } from "rxjs";
import { UserImportLog } from "../model/userImportLog";
import { SignalRService } from "./signalr.service";
import { UpdateLogRequest } from "../model/updateLogStateRequest";
import { AppConfig } from "../model/config";
import * as moment from "moment";
import { ImportLogType } from "../common/enums";
import { SettingsService } from "./settings.service";
import { CustomError } from "../model/Errors/customErrors";

@Injectable()
export class UserImportLogService {
  private readonly userImportLogServiceBaseUrl: string;
  private readonly userImportLogsSubject: BehaviorSubject<UserImportLog[]> = new BehaviorSubject([]);

  private readonly lastImportLogSubject: BehaviorSubject<UserImportLog> = new BehaviorSubject(undefined);

  public lastImportLog = this.lastImportLogSubject.asObservable();
  public userImportLogs = this.userImportLogsSubject.asObservable();

  constructor(
    private readonly configuration: AppConfig,
    private readonly httpClient: HttpHelper,
    private readonly signalRService: SignalRService,
    private readonly settingsService: SettingsService
  ) {
    this.userImportLogServiceBaseUrl = this.configuration.backendUrl + "/api/UserImport";
  }

  init() {
    this.setupSubscription();
    this.loadData();
    this.loadLastImportLog().then();
  }

  public loadData() {

    let logsFrom = new Date();
    logsFrom.setDate(new Date().getDate() - 120);
    let logsTo = new Date();
    let logsFromIso = moment(logsFrom.toISOString()).format('YYYY-MM-DD');
    let logsToIso = moment(logsTo.toISOString()).format('YYYY-MM-DD');


    let userImportLogs: UserImportLog[] = [];
    let currentAirport = this.settingsService.getCurrentAirport();

    this.getImportLogs(currentAirport, logsFromIso, logsToIso).then((result) => {
      if (!result) { return; }
      result.forEach((logResult) => {

        let log = new UserImportLog(
          logResult.id,
          logResult.dateKey,
          logResult.type,
          logResult.message,
          logResult.resolved,
          logResult.affectedResourceId,
          logResult.affectedResourceName,
          logResult.updatedByUserMappingId,
          logResult.updatedBy,
          logResult.airportId,
          new Date(logResult.createdAt)
        );

        userImportLogs.push(log);
      });

      this.sortByCreatedAt(userImportLogs);

      this.userImportLogsSubject.next(userImportLogs);
    });
  }

  public updateImportLogResolvedState(logStateRequest: UpdateLogRequest) {
    const url = `${this.userImportLogServiceBaseUrl}/log/resolve`;
    return this.httpClient.patch(url, logStateRequest,
      () => { },
      (error) => {
        if (error.status === 401) {
          throw new CustomError(
            {
              name: 'Unauthorized',
              message: 'Unauthorized from server',
              cause: error
            });
        }
      }).toPromise<any[]>()
  }

  private setupSubscription() {
    this.signalRService.updateImportLog$.subscribe((importLog: UserImportLog) => {
      let currentItems: UserImportLog[] = this.userImportLogsSubject.getValue();

      if (importLog.type === ImportLogType.LastImport) {
        this.lastImportLogSubject.next(importLog);
      }

      let existingIndex = currentItems.findIndex(u => u.id === importLog.id);

      if (existingIndex >= 0) {
        currentItems[existingIndex] = importLog;
      }
      else {
        currentItems.push(importLog);
      }

      this.sortByCreatedAt(currentItems);

      this.userImportLogsSubject.next(currentItems);

    });
  }

  private sortByCreatedAt(userImportLogs: UserImportLog[]) {
    userImportLogs.sort(this.compare)
  }

  private compare(a: UserImportLog, b: UserImportLog) {
  if (a.createdAt > b.createdAt) {
    return -1;
  }
  if (a.createdAt < b.createdAt) {
    return 1;
  }
  return 0;
}

  private async loadLastImportLog() {
    let result = await this.getLastImport();
    this.lastImportLogSubject.next(result);
  }

  private async getLastImport(): Promise<UserImportLog> {

    return this.getLastImportInternal().then((result) => {
      if (!result) { return; }
      return new UserImportLog(
        result.id,
        result.dateKey,
        result.type,
        result.message,
        result.resolved,
        result.affectedResourceId,
        result.affectedResourceName,
        result.updatedByUserMappingId,
        result.updatedBy,
        result.airportId,
        new Date(result.createdAt)
      );
    });
  }

  private getLastImportInternal(): Promise<UserImportLog> {
    let currentAirport = this.settingsService.getCurrentAirport();

    const url = `${this.userImportLogServiceBaseUrl}/GetLatestImportTimestamp/${currentAirport}`;
    return this.httpClient.get(url,
      () => { },
      (error) => {
        if (error.status === 401) {
          throw new CustomError(
            {
              name: 'Unauthorized',
              message: 'Unauthorized from server',
              cause: error
            });
        }
    }).toPromise<UserImportLog>();
  }

  private getImportLogs(airportId: string, fromDate: string, toDate: string): Promise<UserImportLog[]> {
    const url = `${this.userImportLogServiceBaseUrl}/logs/${airportId}?from=${fromDate}&to=${toDate}`;
    return this.httpClient.get(url,
      () => { },
      (error) => {
        if (error.status === 401) {
          throw new CustomError(
            {
              name: 'Unauthorized',
              message: 'Unauthorized from server',
              cause: error
            });
        }
      }).toPromise<UserImportLog[]>()
  }
}
